import React, { useState, useEffect } from 'react';
import { getUsageEvents } from '../../lib/agents/usageTracking';
import { TOOL_IDS, USAGE_EVENT_TYPES } from '../../lib/agents/firestore-schema';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { useToast } from '../../hooks/use-toast';
import { 
  Activity, 
  Filter, 
  BarChart3, 
  Users, 
  Wrench, 
  Clock, 
  RefreshCw, 
  Calendar,
  Layers,
  Sparkles
} from 'lucide-react';

const TOOL_LABELS = {
  [TOOL_IDS.PLACEHOLDER]: 'Placeholder',
  [TOOL_IDS.FINANCIAL_NEED_ANALYSIS]: 'Financial Need Analysis',
  [TOOL_IDS.INVESTMENT_COMPARISON]: 'Investment Comparison',
  [TOOL_IDS.IMO_COMPENSATION]: 'IMO Compensation',
};

const EVENT_LABELS = {
  [USAGE_EVENT_TYPES.TOOL_OPEN]: 'Tool open',
  [USAGE_EVENT_TYPES.STEP_COMPLETED]: 'Step completed',
  [USAGE_EVENT_TYPES.REPORT_GENERATED]: 'Report generated',
  [USAGE_EVENT_TYPES.REPORT_SHARED]: 'Report shared',
  [USAGE_EVENT_TYPES.EXPORT_PDF]: 'PDF export',
  [USAGE_EVENT_TYPES.TIME_SPENT]: 'Time spent',
};

function formatDate(val) {
  if (!val) return '—';
  if (val.toMillis) return new Date(val.toMillis()).toLocaleString();
  return new Date(val).toLocaleString();
}

export default function AdminAgentUsage() {
  const { toast } = useToast();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toolFilter, setToolFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const filters = { maxResults: 300 };
      if (toolFilter) filters.toolId = toolFilter;
      if (emailFilter) filters.userEmail = emailFilter.trim().toLowerCase();
      if (startDate) filters.startDate = new Date(startDate);
      if (endDate) filters.endDate = new Date(endDate + 'T23:59:59');
      const data = await getUsageEvents(filters);
      setEvents(data);
    } catch (err) {
      toast({ title: 'Error loading analytics', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const byTool = events.reduce((acc, e) => {
    acc[e.toolId] = (acc[e.toolId] || 0) + 1;
    return acc;
  }, {});
  const byUser = events.reduce((acc, e) => {
    const k = e.userEmail || e.userId || 'unknown';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const byEventType = events.reduce((acc, e) => {
    acc[e.eventType] = (acc[e.eventType] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6 text-white font-opensans">
      {/* Header Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-[#0D1B2A]/90 border border-[#C9A84C]/30 p-6 rounded-2xl shadow-xl backdrop-blur-xl">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#162236] border border-[#C9A84C]/30 text-xs font-semibold text-[#E8D4A0] uppercase tracking-wider">
            <Activity className="w-3.5 h-3.5 text-[#C9A84C]" />
            Agent Portal Metrics
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold font-playfair text-white tracking-wide">
            Usage & Analytics Tracker
          </h1>
          <p className="text-sm text-slate-400 max-w-2xl">
            Real-time usage insights across agent tools, user interactions, and event triggers logged in Firestore.
          </p>
        </div>
        <Button
          onClick={load}
          disabled={loading}
          className="bg-gradient-to-r from-[#C9A84C] to-[#8C6D23] hover:from-[#d4b455] hover:to-[#997728] text-[#070E1B] font-bold shadow-lg transition-all rounded-xl gap-2 self-start sm:self-auto"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh Analytics
        </Button>
      </div>

      {/* Filter Card */}
      <Card className="bg-[#0D1B2A]/90 border border-[#C9A84C]/30 shadow-xl rounded-2xl overflow-hidden">
        <CardHeader className="border-b border-[#C9A84C]/20 pb-4">
          <CardTitle className="text-base font-bold font-playfair text-[#E8D4A0] flex items-center gap-2">
            <Filter className="w-4 h-4 text-[#C9A84C]" />
            Filter Usage Events
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-300">Tool</Label>
              <select
                className="w-full bg-[#162236] border border-[#C9A84C]/30 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C9A84C]"
                value={toolFilter}
                onChange={(e) => setToolFilter(e.target.value)}
              >
                <option value="" className="bg-[#0D1B2A]">All Tools</option>
                {Object.entries(TOOL_LABELS).map(([id, label]) => (
                  <option key={id} value={id} className="bg-[#0D1B2A]">{label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-300">User Email</Label>
              <Input
                className="bg-[#162236] border-[#C9A84C]/30 text-white rounded-xl placeholder:text-slate-500 focus:border-[#C9A84C] text-sm"
                placeholder="email@example.com"
                value={emailFilter}
                onChange={(e) => setEmailFilter(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-300">From Date</Label>
              <Input 
                type="date" 
                className="bg-[#162236] border-[#C9A84C]/30 text-white rounded-xl focus:border-[#C9A84C] text-sm" 
                value={startDate} 
                onChange={(e) => setStartDate(e.target.value)} 
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-300">To Date</Label>
              <Input 
                type="date" 
                className="bg-[#162236] border-[#C9A84C]/30 text-white rounded-xl focus:border-[#C9A84C] text-sm" 
                value={endDate} 
                onChange={(e) => setEndDate(e.target.value)} 
              />
            </div>

            <div>
              <Button 
                onClick={load} 
                disabled={loading}
                className="w-full bg-[#162236] hover:bg-[#1f2d47] border border-[#C9A84C]/50 text-[#C9A84C] font-semibold rounded-xl transition-all"
              >
                {loading ? 'Applying...' : 'Apply Filters'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* By Tool */}
        <Card className="bg-[#0D1B2A]/90 border border-[#C9A84C]/30 shadow-xl rounded-2xl">
          <CardHeader className="border-b border-[#C9A84C]/20 pb-3">
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-[#E8D4A0] flex items-center justify-between">
              <span>By Tool</span>
              <Wrench className="w-4 h-4 text-[#C9A84C]" />
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {Object.entries(byTool).length === 0 ? (
              <p className="text-slate-400 text-sm italic">No data recorded</p>
            ) : (
              <ul className="space-y-2.5">
                {Object.entries(byTool).map(([id, count]) => (
                  <li key={id} className="flex items-center justify-between text-sm bg-[#162236] p-2.5 rounded-xl border border-[#C9A84C]/10">
                    <span className="text-slate-200 font-medium">{TOOL_LABELS[id] || id}</span>
                    <span className="bg-[#C9A84C]/20 text-[#C9A84C] font-bold text-xs px-2.5 py-1 rounded-full border border-[#C9A84C]/30">
                      {count}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* By Event Type */}
        <Card className="bg-[#0D1B2A]/90 border border-[#C9A84C]/30 shadow-xl rounded-2xl">
          <CardHeader className="border-b border-[#C9A84C]/20 pb-3">
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-[#E8D4A0] flex items-center justify-between">
              <span>By Event Type</span>
              <Layers className="w-4 h-4 text-[#C9A84C]" />
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {Object.entries(byEventType).length === 0 ? (
              <p className="text-slate-400 text-sm italic">No data recorded</p>
            ) : (
              <ul className="space-y-2.5">
                {Object.entries(byEventType).map(([id, count]) => (
                  <li key={id} className="flex items-center justify-between text-sm bg-[#162236] p-2.5 rounded-xl border border-[#C9A84C]/10">
                    <span className="text-slate-200 font-medium">{EVENT_LABELS[id] || id}</span>
                    <span className="bg-[#C9A84C]/20 text-[#C9A84C] font-bold text-xs px-2.5 py-1 rounded-full border border-[#C9A84C]/30">
                      {count}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Top Active Users */}
        <Card className="bg-[#0D1B2A]/90 border border-[#C9A84C]/30 shadow-xl rounded-2xl">
          <CardHeader className="border-b border-[#C9A84C]/20 pb-3">
            <CardTitle className="text-sm font-bold uppercase tracking-wider text-[#E8D4A0] flex items-center justify-between">
              <span>Top Active Users</span>
              <Users className="w-4 h-4 text-[#C9A84C]" />
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {Object.entries(byUser).length === 0 ? (
              <p className="text-slate-400 text-sm italic">No data recorded</p>
            ) : (
              <ul className="space-y-2.5 max-h-56 overflow-y-auto pr-1">
                {Object.entries(byUser)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 10)
                  .map(([email, count]) => (
                    <li key={email} className="flex items-center justify-between text-sm bg-[#162236] p-2.5 rounded-xl border border-[#C9A84C]/10">
                      <span className="text-slate-200 font-medium truncate max-w-[180px]" title={email}>
                        {email}
                      </span>
                      <span className="bg-[#C9A84C]/20 text-[#C9A84C] font-bold text-xs px-2.5 py-1 rounded-full border border-[#C9A84C]/30">
                        {count} events
                      </span>
                    </li>
                  ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Usage Log Table */}
      <Card className="bg-[#0D1B2A]/90 border border-[#C9A84C]/30 shadow-xl rounded-2xl overflow-hidden">
        <CardHeader className="border-b border-[#C9A84C]/20 pb-4">
          <CardTitle className="text-base font-bold font-playfair text-[#E8D4A0] flex items-center justify-between">
            <span className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-[#C9A84C]" />
              Recent Usage Event Logs
            </span>
            <span className="text-xs font-normal text-slate-400">
              Showing total {events.length} events
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-slate-400">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-[#C9A84C]" />
              Loading usage analytics...
            </div>
          ) : events.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              No usage events match the selected filters.
            </div>
          ) : (
            <table className="w-full text-sm text-left text-slate-300">
              <thead className="bg-[#162236] text-[#E8D4A0] text-xs font-bold uppercase tracking-wider border-b border-[#C9A84C]/20">
                <tr>
                  <th className="px-6 py-3.5">Timestamp</th>
                  <th className="px-6 py-3.5">User</th>
                  <th className="px-6 py-3.5">Tool</th>
                  <th className="px-6 py-3.5">Event Type</th>
                  <th className="px-6 py-3.5">Payload</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#162236]">
                {events.slice(0, 100).map((e) => (
                  <tr key={e.id} className="hover:bg-[#162236]/60 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-400 font-mono">
                      {formatDate(e.createdAt)}
                    </td>
                    <td className="px-6 py-4 font-semibold text-white">
                      {e.userEmail || e.userId || 'Anonymous'}
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#162236] border border-[#C9A84C]/30 text-xs font-semibold text-[#E8D4A0]">
                        {TOOL_LABELS[e.toolId] || e.toolId}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-slate-300 text-xs font-semibold uppercase tracking-wider">
                        {EVENT_LABELS[e.eventType] || e.eventType}
                      </span>
                    </td>
                    <td className="px-6 py-4 max-w-xs">
                      {e.payload ? (
                        <code className="bg-[#070E1B] text-[#C9A84C] font-mono text-xs px-2 py-1 rounded border border-[#C9A84C]/20 block truncate max-w-xs" title={JSON.stringify(e.payload)}>
                          {JSON.stringify(e.payload)}
                        </code>
                      ) : (
                        <span className="text-slate-500 font-mono text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
